import { useQuery, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { getAllRoles, getRolePermissions } from "@/services/apis/rbacApi";
import { getRolesFromToken, hasAppPermision } from "@/helpers/jwt.helpers";
import { FEATURE_PERMISSION_MAPPING, PermissionKey } from "@/constants/rbac";
import { ADMIN_ROLES } from "@/constants";
import { AxiosError } from "axios";
import { getErrorObject } from "@/helpers/http.helpers";

export interface Permission {
  permissionId: number;
  name: string;
  description: string;
}

export interface Role {
  roleId: number;
  name: string;
  roleName: string;
  description: string;
  customRole: boolean;
  productId: number;
  parentRoles: any[]; // Assuming parentRoles can be of any type for now
  /**
   * Saras-staff-only persona flag from the user service. Only present because
   * `API.RBAC.role` asks for `internal=true` (the FULL catalog); the default
   * response omits internal roles entirely, which broke own-role capability
   * resolution for internal users.
   */
  internal?: boolean;
}

/**
 * Is this a Saras-internal (staff-only) role? The catalog now carries every role
 * so capability resolution works for internal users, which means every
 * USER-FACING role picker has to filter these out.
 *
 * The name fallback is deliberate defence-in-depth: the `internal` boolean is
 * additive on the API side, so a row that predates it (or a payload shape that
 * drops it) must still not offer `SarasAdmin` / `IQDashboardAdmin` to a customer.
 */
export const isInternalRole = (role: Partial<Role> | null | undefined): boolean =>
  !!role && (role.internal === true || ADMIN_ROLES.includes(role.name as string));

export interface InvitedUsersParams {
  companyId: number;
  productType: 'IQ';
  page?: number;
  size?: number;
  search?: string;
  roleId?: number;
}
export interface RoleWithPermissions extends Role {
  permissions: Permission[];
}

export type IQAccess = {
  [key in PermissionKey]: boolean;
};

export const useRbac = (currentUser: any, authTokenCookie: string | null) => {
  // Step 1: Fetch the initial list of roles
  const { data: roles, isLoading: areRolesLoading, isPending: rolesPending, error: rolesError } = useQuery<RoleWithPermissions[], AxiosError>({
    queryKey: ["roles"],
    enabled: !!currentUser && !!authTokenCookie,
    // Wrap so react-query's QueryFunctionContext isn't forwarded as an axios config
    // (getAllRoles takes an optional one). No global toast for this endpoint — the
    // failure surfaces through `rolesError` → `AuthContext.roleAccessError`, and the
    // share dialog degrades to an empty picker; that shared policy is what lets both
    // callers use this one `["roles"]` cache entry.
    queryFn: () => getAllRoles(),
    staleTime: Infinity,
  });

  // The role NAMES carried by the user's own JWT. `hasAppPermision` intersects the
  // per-role permission matrix with exactly this set, so every other role's
  // permissions are fetched and thrown away — boot paid for the whole company's
  // matrix to answer a question about one user.
  const ownRoleNames: string[] = useMemo(
    () => (authTokenCookie ? getRolesFromToken(authTokenCookie) || [] : []),
    [authTokenCookie]
  );

  // Step 2: Use `useQueries` to fetch permissions for each role once roles are
  // available. Only the user's OWN roles actually fetch; the rest stay disabled
  // placeholders so the array still lines up 1:1 with `roles` (the admin-elevation
  // branch in `hasAppPermision` keys off the list being non-empty) and so the
  // shared `["permission", roleId]` cache keys are unchanged.
  const permissionQueries = useQueries({
    queries: (roles || []).map((role) => ({
      queryKey: ["permission", role.roleId],
      queryFn: () => getRolePermissions(role.roleId),
      staleTime: Infinity,
      enabled: !!roles && ownRoleNames.includes(role.name),
    })),
  });

  // Step 3: Combine the data and calculate loading states. Loading tracks only the
  // user's own roles — access no longer waits on roles the user doesn't hold.
  const arePermissionsLoading = permissionQueries.some(
    (query, index) => ownRoleNames.includes((roles || [])[index]?.name) && query.isLoading
  );

  const rolesWithPermissions: RoleWithPermissions[] = (roles || []).map((role, index) => ({
    ...role,
    permissions: permissionQueries[index].data?.permissions || [],
  }));

  const access: IQAccess = useMemo(() => {
    if(!authTokenCookie){
      return {} as IQAccess;
    }
    if (arePermissionsLoading) {
      // While permissions are loading, return a default 'denied' state.
      // This can be customized to your needs.
      const initialAccess: Partial<IQAccess> = {};
      Object.keys(FEATURE_PERMISSION_MAPPING).forEach(key => {
        initialAccess[key as PermissionKey] = false;
      });
      return initialAccess as IQAccess;
    }
    // Once all permissions are loaded, calculate the final access object.
    return hasAppPermision(authTokenCookie, rolesWithPermissions);
  }, [arePermissionsLoading, rolesWithPermissions, authTokenCookie]);

  const permissionError = permissionQueries.find((query) => query.error)?.error 

  return {
    data: access,
    roles,
    permissions: permissionQueries.map((query) => query.data?.permissions || []),
    isLoading: areRolesLoading || arePermissionsLoading,
    // Scoped to the user's own roles so the never-enabled placeholder queries (which
    // stay `isPending`) don't change what this reports. The expression itself reads
    // inverted, but it is preserved as-is: nothing consumes `isPending` today
    // (AuthContext re-exports it as `roleAccessPending` and no component reads that),
    // so flipping it would be an unverifiable behavior change.
    isPending:
      rolesPending ||
      permissionQueries.every(
        (query, index) =>
          !ownRoleNames.includes((roles || [])[index]?.name) || !query.isPending
      ),
    error: rolesError 
    ? getErrorObject(rolesError) 
    : permissionError
      ? getErrorObject(permissionError)
      : undefined,
  };
};
